import { create } from 'zustand';

import type { ChannelStatus, ConnectionStatus } from '../types';

/**
 * Store state for WebSocket connection monitoring across multiple channels
 */
interface ConnectionStatusState {
  /**
   * Map of channel IDs to their current status
   * @example { 'htx-orderbook-BTCUSDT': { id: 'htx-orderbook-BTCUSDT', status: 'connected', ... } }
   */
  channels: Record<string, ChannelStatus>;

  /**
   * Updates a channel's status (merges partial updates)
   * @param channelId - Unique channel identifier
   * @param updates - Partial status updates to merge
   */
  updateChannelStatus: (channelId: string, updates: Partial<ChannelStatus>) => void;

  /**
   * Removes a channel from tracking (called on unsubscribe)
   * @param channelId - Channel identifier to remove
   */
  removeChannel: (channelId: string) => void;

  /**
   * Resets all channels to initial state
   */
  reset: () => void;

  /**
   * Gets status for a specific channel
   * @param channelId - Channel identifier
   * @returns Channel status or undefined if not found
   */
  getChannelStatus: (channelId: string) => ChannelStatus | undefined;
}

/**
 * Initial state for a new channel
 */
const createInitialChannelStatus = (channelId: string): ChannelStatus => ({
  id: channelId,
  status: 'connecting' as ConnectionStatus,
  lastMessageTimestamp: null,
  isHeartbeatActive: false,
  connectionAttempts: 0,
  sessionToken: null,
  fault: undefined, // No fault initially
});

/**
 * Zustand store for tracking WebSocket connection status across multiple channels
 *
 * This store monitors connection health for all WebSocket channels managed by
 * the WebSocketConnector. Each channel tracks:
 * - Connection status (connecting/connected/disconnected/error/heartbeat/resync_failed)
 * - Last message timestamp for calculating data freshness
 * - Heartbeat mode indicator (when using REST fallback)
 * - Connection attempt counter (for retry tracking)
 * - Session token (for debugging stale updates)
 * - Fault information (for error diagnosis and telemetry)
 *
 * Used by ConnectionStatusChip components in various widgets to display
 * real-time connection health per channel.
 *
 * @example
 * ```ts
 * // In WebSocketConnector
 * const store = useConnectionStatusStore.getState();
 * store.updateChannelStatus('htx-orderbook-BTCUSDT', {
 *   status: 'connected',
 *   lastMessageTimestamp: Date.now(),
 * });
 * ```
 *
 * @example
 * ```tsx
 * // In UI component
 * const channelStatus = useConnectionStatusStore(
 *   (state) => state.channels['htx-orderbook-BTCUSDT']
 * );
 * <ConnectionStatusChip
 *   connectionStatus={channelStatus?.status}
 *   lastMessageTimestamp={channelStatus?.lastMessageTimestamp}
 * />
 * ```
 */
export const useConnectionStatusStore = create<ConnectionStatusState>((set, get) => ({
  channels: {},

  updateChannelStatus: (channelId: string, updates: Partial<ChannelStatus>) => {
    set(state => {
      const existingChannel = state.channels[channelId];

      // Create base channel (either existing or new)
      let updatedChannel: ChannelStatus;

      if (!existingChannel) {
        // Create new channel with initial status
        updatedChannel = {
          ...createInitialChannelStatus(channelId),
          ...updates,
        };
      } else {
        // Merge updates into existing channel
        updatedChannel = {
          ...existingChannel,
          ...updates,
        };
      }

      // Use existing or provided timestamp for recent data check
      const relevantTimestamp = updates.lastMessageTimestamp ?? updatedChannel.lastMessageTimestamp;

      // Smart status handling: if we're receiving data, override disconnect/error
      if (relevantTimestamp) {
        const timeSinceMessage = Date.now() - relevantTimestamp;

        // If we received data recently (within 10 seconds) and trying to set
        // disconnected/error, ignore it - connection is actually working
        if (timeSinceMessage < 10000) {
          if (updates.status === 'disconnected' || updates.status === 'error') {
            console.log(
              `[ConnectionStatusStore] Ignoring ${
                updates.status
              } for ${channelId} - received data ${Math.floor(timeSinceMessage / 1000)}s ago`,
            );
            updatedChannel.status = 'connected';
          } else if (existingChannel && existingChannel.status !== 'connected' && !updates.status) {
            // If we're receiving data but status is not connected, update it
            console.log(
              `[ConnectionStatusStore] Received data for ${channelId} while status is ${existingChannel.status}, updating to connected`,
            );
            updatedChannel.status = 'connected';
          }
        }
      }

      // When status changes to 'connected' (updating existing), initialize timestamp if not set
      if (
        updates.status === 'connected' &&
        existingChannel &&
        !updatedChannel.lastMessageTimestamp
      ) {
        updatedChannel.lastMessageTimestamp = Date.now();
      }

      // Increment connection attempts when status changes to 'connecting' (only if not explicitly provided)
      if (updates.status === 'connecting' && updates.connectionAttempts === undefined) {
        updatedChannel.connectionAttempts = (existingChannel?.connectionAttempts ?? 0) + 1;
      }

      // Reset connection attempts on successful connection
      if (updates.status === 'connected') {
        updatedChannel.connectionAttempts = 0;
      }

      return {
        channels: {
          ...state.channels,
          [channelId]: updatedChannel,
        },
      };
    });
  },

  removeChannel: (channelId: string) => {
    set(state => {
      const { [channelId]: _removed, ...remainingChannels } = state.channels;
      return { channels: remainingChannels };
    });
  },

  reset: () => {
    set({ channels: {} });
  },

  getChannelStatus: (channelId: string) => {
    return get().channels[channelId];
  },
}));

/**
 * Selector hook to get status for a specific channel
 * Optimized to prevent unnecessary re-renders
 *
 * @example
 * ```tsx
 * const status = useChannelStatus('htx-orderbook-BTCUSDT');
 * ```
 */
export const useChannelStatus = (channelId: string): ChannelStatus | undefined => {
  return useConnectionStatusStore(state => state.channels[channelId]);
};

/**
 * Selector hook to get all channel statuses
 *
 * @example
 * ```tsx
 * const allStatuses = useAllChannelStatuses();
 * ```
 */
export const useAllChannelStatuses = (): Record<string, ChannelStatus> => {
  return useConnectionStatusStore(state => state.channels);
};

/**
 * Selector hook to get array of channel statuses (useful for lists)
 *
 * @example
 * ```tsx
 * const statusList = useChannelStatusList();
 * statusList.forEach(status => console.log(status.id, status.status));
 * ```
 */
export const useChannelStatusList = (): ChannelStatus[] => {
  return useConnectionStatusStore(state => Object.values(state.channels));
};
